import { useCallback, useEffect, useState } from 'react';
import { Button, Card, EmptyState, Field, PageHeader, Spinner, StatusPill, Textarea } from '../components/ui';
import { Dialog } from '../components/dialogs/Dialog';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import { UsersAPI } from '../services/endpoints/admin';
import { useDispatchAction } from '../hooks/useDispatchAction';
import { session, type Role } from '../services/auth';
import type { User } from '../types/api';
import { relativeTime } from '../utils/format';
import './common.css';
import './AccountsPage.css';

const STAFF_ROLES: Role[] = ['ADMIN', 'CREATOR'];
const ALL_ROLES: Role[] = ['USER', 'CREATOR', 'ADMIN'];

export function AccountsPage() {
  const [accounts, setAccounts] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [managing, setManaging] = useState<User | null>(null);
  const [removeAdminConfirm, setRemoveAdminConfirm] = useState<{ user: User; nextRoles: Role[]; reason: string } | null>(null);
  const { run } = useDispatchAction();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const responses = await Promise.all(
        STAFF_ROLES.map(role => UsersAPI.list('', 0, 200, role).catch(() => null)),
      );
      const merged = new Map<string, User>();
      for (const resp of responses) {
        if (!resp?.data) continue;
        for (const u of resp.data) merged.set(u.id, u);
      }
      const list = Array.from(merged.values()).sort((a, b) =>
        a.email.localeCompare(b.email),
      );
      setAccounts(list);
      if (responses.every(r => r === null)) {
        setError('endpoint');
      }
    } catch {
      setError('endpoint');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function applyRoles(target: User, nextRoles: Role[], reason: string) {
    await run('Update roles', () => UsersAPI.setRoles(target.id, nextRoles, reason));
    setManaging(null);
    setRemoveAdminConfirm(null);
    refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="11 — ACCOUNTS"
        title={<>Admin <em>accounts</em>.</>}
        lede="Internal staff with ADMIN or CREATOR access. Use Users for end-user management. Role changes are written to the audit log."
      />

      {error === 'endpoint' && (
        <Card>
          <div className="nb-accounts__notice">
            <strong>Endpoint pending.</strong> This page needs <code>GET /api/admin/users?role=ADMIN</code> and{' '}
            <code>POST /api/admin/users/&#123;id&#125;/roles</code> on the backend. Once the admin namespace ships, the table populates automatically.
          </div>
        </Card>
      )}

      {loading ? (
        <Card><Spinner /> Loading…</Card>
      ) : accounts.length === 0 ? (
        <Card>
          <EmptyState
            title="No staff accounts"
            hint="Promote an existing user to ADMIN or CREATOR via Users → Manage roles."
          />
        </Card>
      ) : (
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {accounts.map(u => {
                const isSelf = session.user?.id === u.id;
                return (
                  <tr key={u.id}>
                    <td>
                      <span className="nb-table__primary">{u.email}</span>
                      {isSelf && <span className="nb-accounts__self">YOU</span>}
                    </td>
                    <td>{[u.firstname, u.lastname].filter(Boolean).join(' ') || '—'}</td>
                    <td>
                      <div className="nb-accounts__roles">
                        {u.roles.map(r => (
                          <StatusPill key={r} tone={r === 'ADMIN' ? 'ember' : r === 'CREATOR' ? 'brass' : 'moss'}>
                            {r}
                          </StatusPill>
                        ))}
                      </div>
                    </td>
                    <td>
                      {u.disabled
                        ? <StatusPill tone="rust">Suspended</StatusPill>
                        : <StatusPill tone="moss">Active</StatusPill>}
                    </td>
                    <td className="nb-table__mono">{relativeTime(u.lastSeen)}</td>
                    <td className="nb-table__align-right">
                      <Button size="sm" onClick={() => setManaging(u)}>Manage roles</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {managing && (
        <ManageRolesDialog
          open
          user={managing}
          isSelf={session.user?.id === managing.id}
          onClose={() => setManaging(null)}
          onSave={(nextRoles, reason) => {
            const target = managing;
            const losingAdmin = target.roles.includes('ADMIN') && !nextRoles.includes('ADMIN');
            if (losingAdmin) {
              setRemoveAdminConfirm({ user: target, nextRoles, reason });
            } else {
              applyRoles(target, nextRoles, reason);
            }
          }}
        />
      )}

      <ConfirmDialog
        open={!!removeAdminConfirm}
        title="Revoke ADMIN role"
        message={removeAdminConfirm
          ? `Revoking ADMIN from ${removeAdminConfirm.user.email} removes their access to this admin app and audit log immediately.`
          : ''}
        destructive
        confirmLabel="Revoke ADMIN"
        onClose={() => setRemoveAdminConfirm(null)}
        onConfirm={() => {
          if (!removeAdminConfirm) return;
          return applyRoles(removeAdminConfirm.user, removeAdminConfirm.nextRoles, removeAdminConfirm.reason);
        }}
      />
    </div>
  );
}

function ManageRolesDialog({ open, user, isSelf, onClose, onSave }: {
  open: boolean;
  user: User;
  isSelf: boolean;
  onClose: () => void;
  onSave: (roles: Role[], reason: string) => void;
}) {
  const [roles, setRoles] = useState<Role[]>(user.roles);
  const [reason, setReason] = useState('');

  function toggle(role: Role) {
    setRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  }

  const blockSelfAdminRemoval = isSelf && user.roles.includes('ADMIN') && !roles.includes('ADMIN');
  const noChange = roles.length === user.roles.length && roles.every(r => user.roles.includes(r));
  const canSave = !!reason.trim() && !noChange && roles.length > 0 && !blockSelfAdminRemoval;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Manage roles · ${user.email}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="ember" onClick={() => onSave(roles, reason.trim())} disabled={!canSave}>Save roles</Button>
        </>
      }
    >
      <div className="nb-accounts__role-grid">
        {ALL_ROLES.map(role => {
          const checked = roles.includes(role);
          return (
            <label key={role} className={`nb-accounts__role ${checked ? 'is-on' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(role)} />
              <span className="nb-accounts__role-name">{role}</span>
              <span className="nb-accounts__role-hint">
                {role === 'ADMIN' && 'Full access to this admin app and audit log.'}
                {role === 'CREATOR' && 'Can publish media and edit transcripts.'}
                {role === 'USER' && 'Default end-user role.'}
              </span>
            </label>
          );
        })}
      </div>

      {blockSelfAdminRemoval && (
        <div className="nb-accounts__warn">
          You can't remove your own ADMIN role. Ask another admin to do it.
        </div>
      )}

      {roles.length === 0 && (
        <div className="nb-accounts__warn">An account must keep at least one role.</div>
      )}

      <Field label="Reason (recorded in audit log)">
        <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this change being made?" />
      </Field>
    </Dialog>
  );
}
